import {Admin, DocumentRow, InputFile} from "@components";
import {EmptyData} from "@components/EmptyData";
import {DocumentIcon, ExpandMoreIcon} from "@components/icons";
import {api} from "@core/api";
import {Artifact, Asset, Document, Goods, Task, Ticket} from "@core/model";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {translate} from "@util";
import {useDispatch} from "react-redux";

const Documents = ({
  type,
  entity,
  sx,
  component = "accordion",
}: {
  type: "asset" | "ticket" | "task" | "goods" | "artifact";
  entity: Asset | Task | Ticket | Goods | Artifact | undefined;
  sx?: SxProps<Theme>;
  component?: "accordion" | "box";
}) => {
  const dispatch = useDispatch();
  const {theme} = useThemeContext();
  const [uplodad, {isLoading: isUploading}] = api.useUploadDocumentsMutation();

  const reloadData = () =>
    dispatch(api.util.invalidateTags([{type, id: entity!.id}]));

  const handleConfirmUpload = (files: File[]) => {
    uplodad({[`${type}Id`]: entity!.id, files})
      .unwrap()
      .then(reloadData);
  };

  if (component === "box") {
    return (
      <Box sx={{...sx}}>
        {entity?.documents?.length === 0 ? (
          <EmptyData
            Icon={DocumentIcon}
            sx={{px: 1, py: 2}}
            message={translate("common.label.empty.documents")}
          />
        ) : (
          entity?.documents?.map((d: Document) => (
            <DocumentRow key={d.id} document={d} onDelete={reloadData} />
          ))
        )}
        <Admin>
          <Box p={1} />
          <InputFile
            loading={isUploading}
            multiple
            onConfirm={handleConfirmUpload}
          />
        </Admin>
      </Box>
    );
  }

  return (
    <Accordion defaultExpanded sx={{...sx}}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction={"row"} gap={2} alignItems={"center"}>
          <DocumentIcon />
          <Typography fontWeight={500} variant="body1" lineHeight={1}>
            {translate("common.label.documents")}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}>
        {entity?.documents?.length === 0 ? (
          <EmptyData
            Icon={DocumentIcon}
            sx={{px: 1, py: 2}}
            message={translate("common.label.empty.documents")}
          />
        ) : (
          entity?.documents?.map((d: Document) => (
            <DocumentRow key={d.id} document={d} onDelete={reloadData} />
          ))
        )}
        <Admin>
          <Box p={1} />
          <InputFile
            loading={isUploading}
            multiple
            onConfirm={handleConfirmUpload}
          />
        </Admin>
      </AccordionDetails>
    </Accordion>
  );
};

export {Documents};
